import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface Column<Row> {
  key: keyof Row | string;
  header: string;
  render?: (row: Row) => ReactNode;
  sortable?: boolean;
  csv?: (row: Row) => string;
  /** Opt out of single-line truncation for this column (e.g. action buttons). */
  noTruncate?: boolean;
}

interface Props<Row> {
  columns: Column<Row>[];
  rows: Row[];
  pageSize?: 20 | 50 | 100;
  loading?: boolean;
  empty?: string;
  rowKey: (r: Row) => string;
  onRowClick?: (r: Row) => void;
}

/**
 * Part F — universal data grid (sortable, paginated, CSV export).
 * Server pagination is owned by the page; this component handles client-side
 * windowing for already-loaded results.
 */
export function DataGrid<Row>({
  columns,
  rows,
  pageSize: initialPageSize = 20,
  loading,
  empty,
  rowKey,
  onRowClick,
}: Props<Row>) {
  const { t } = useTranslation();
  const [pageSize, setPageSize] = useState<20 | 50 | 100>(initialPageSize);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const k = sort.key;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = (a as Record<string, unknown>)[k];
      const vb = (b as Record<string, unknown>)[k];
      if (va == null && vb == null) return 0;
      if (va == null) return -dir;
      if (vb == null) return dir;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [rows, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const slice = sorted.slice(page * pageSize, (page + 1) * pageSize);

  const exportCsv = () => {
    const header = columns.map((c) => csvEscape(c.header)).join(',');
    const body = sorted
      .map((r) =>
        columns
          .map((c) =>
            csvEscape(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              c.csv ? c.csv(r) : String((r as any)[c.key] ?? '')
            )
          )
          .join(',')
      )
      .join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <div className="flex justify-between items-center px-3 py-2 border-b text-sm">
        <div className="text-muted-foreground">{t('ui.rowsCount', { count: sorted.length })}</div>
        <div className="flex items-center gap-2">
          <select
            className="text-xs h-7 rounded-md border border-input bg-background px-2 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value) as 20 | 50 | 100);
              setPage(0);
            }}
          >
            <option value={20}>{t('ui.perPage', { n: 20 })}</option>
            <option value={50}>{t('ui.perPage', { n: 50 })}</option>
            <option value={100}>{t('ui.perPage', { n: 100 })}</option>
          </select>
          <button
            onClick={exportCsv}
            className="text-xs h-7 rounded-md border border-input bg-background px-2 hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            {t('ui.exportCsv')}
          </button>
        </div>
      </div>
      <div className="relative w-full overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              {columns.map((c) => (
                <th
                  key={String(c.key)}
                  className={`h-10 px-3 text-left align-middle font-medium text-muted-foreground ${c.sortable !== false ? 'cursor-pointer select-none hover:text-foreground' : ''}`}
                  onClick={() => {
                    if (c.sortable === false) return;
                    const k = String(c.key);
                    setSort((cur) =>
                      cur?.key === k
                        ? { key: k, dir: cur.dir === 'asc' ? 'desc' : 'asc' }
                        : { key: k, dir: 'asc' }
                    );
                  }}
                >
                  {c.header}
                  {sort?.key === String(c.key) && (
                    <span className="ml-1 text-xs">{sort.dir === 'asc' ? '▲' : '▼'}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="[&_tr:last-child]:border-0">
            {loading && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-6 text-center text-muted-foreground"
                >
                  {t('common.loading')}
                </td>
              </tr>
            )}
            {!loading && slice.length === 0 && (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-3 py-6 text-center text-muted-foreground"
                >
                  {empty ?? t('ui.noRows')}
                </td>
              </tr>
            )}
            {!loading &&
              slice.map((r) => (
                <tr
                  key={rowKey(r)}
                  className={`border-t transition-colors hover:bg-muted/50 ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={() => onRowClick?.(r)}
                >
                  {columns.map((c) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const raw = (r as any)[c.key];
                    // Full value for the hover tooltip: prefer the CSV projection,
                    // else the raw cell value when there's no custom renderer.
                    const title = c.csv
                      ? c.csv(r)
                      : c.render || raw == null
                        ? undefined
                        : String(raw);
                    return (
                      <td key={String(c.key)} className="p-3 align-middle">
                        <div
                          className={c.noTruncate ? '' : 'truncate max-w-[18rem]'}
                          title={title || undefined}
                        >
                          {c.render ? c.render(r) : raw == null ? '—' : String(raw)}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center px-3 py-2 border-t text-xs text-muted-foreground">
        <div>{t('ui.pageOf', { page: page + 1, total: totalPages })}</div>
        <div className="flex gap-1">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="h-7 rounded-md border border-input bg-background px-2 hover:bg-accent hover:text-accent-foreground disabled:opacity-40 transition-colors"
          >
            {t('ui.prev')}
          </button>
          <button
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            className="h-7 rounded-md border border-input bg-background px-2 hover:bg-accent hover:text-accent-foreground disabled:opacity-40 transition-colors"
          >
            {t('ui.next')}
          </button>
        </div>
      </div>
    </div>
  );
}

function csvEscape(value: string): string {
  if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
  return value;
}
